import React from "react";
import { PercentaVstupy } from "../vstupneKomponenty.js";
import { apiUrl } from "../../config/apiBase.js";
import HintPopover from "../HintPopover.js";
import {
  InvesticneInfoPopoverObsah,
  InvesticnePoplatkyPopoverObsah,
  maInvesticneProduktInfo,
} from "./InvesticneProduktPopoverObsah.js";
import infoCircleIkona from "../../obr/info-circle.svg";
import poplatkyIkona from "../../obr/receipt-euro.svg";
import InvesticneOdporucaniaVystupKarta from "./InvesticneOdporucaniaVystupKarta";
import { vytvorInvestPdfOdporucania, } from "../../utils/investPdfOdporucania";
import { vyhodnotMinimalneInvesticieVah, } from "../../utils/investOdporucaniaVahy";

const PROFILY = {
  konzervativny: "Konzervatívny",
  vyvazeny: "Vyvážený",
  dynamicky: "Dynamický",
};

const PROFIL_SKratky = {
  konzervativny: "K",
  vyvazeny: "V",
  dynamicky: "D",
};

const TRIEDY_AKTIV = [
  ["akcie", "Akcie"],
  ["dlhopisy", "Dlhopisy"],
  ["penazne", "Peňažné"],
  ["zmiesane", "Zmiešané"],
  ["komodity", "Komodity"],
  ["realitne", "Realitné"],
  ["alternativne", "Alternatívne"],
];

const MAX_POCET_VYBRANYCH_RIESENI = 4;
const VLASTNA_VOLBA_KLUC =
  "vlastnaVolba";

function vytvorKlucProduktu(kod) {
  return `produkt:${kod}`;
}

function vytvorRovnomerneVahy(
  polozky
) {
  if (polozky.length === 0) {
    return {};
  }

  const zaklad =
    Math.floor(
      10000 / polozky.length
    ) / 100;

  return polozky.reduce(
    (vahy, polozka, index) => {
      vahy[polozka.kluc] =
        index === polozky.length - 1
          ? Math.round(
              (
                100 -
                zaklad *
                  (
                    polozky.length -
                    1
                  )
              ) * 100
            ) / 100
          : zaklad;

      return vahy;
    },
    {}
  );
}

function vytvorZlozenieVlastnejVolby(
  riadky
) {
  const zlozenieAktiv =
    TRIEDY_AKTIV.reduce(
      (zlozenie, [kluc]) => ({
        ...zlozenie,
        [kluc]: 0,
      }),
      {}
    );

  riadky.forEach((riadok) => {
    if (
      riadok.triedaAktiv &&
      Object.prototype.hasOwnProperty.call(
        zlozenieAktiv,
        riadok.triedaAktiv
      )
    ) {
      zlozenieAktiv[
        riadok.triedaAktiv
      ] += cislo(riadok.podiel);
    }
  });

  return zlozenieAktiv;
}

const vytvorVlastnuVolbu = () => (
  Array.from({ length: 3 }, (_, index) => ({
    id: index + 1,
    nazov: "",
    podiel: 0,
    triedaAktiv: "",
    priemerneZhodnotenie: "",
  }))
);

const cislo = (hodnota) => {
  const vysledok = Number(hodnota);

  return Number.isFinite(vysledok)
    ? vysledok
    : 0;
};

function vytvorKlucIgnorovaniaMinimalnychInvesticii({
  polozky,
  vahy,
  mesacnyVklad,
  jednorazovyVklad,
}) {
  const zoradenePolozky =
    Array.isArray(polozky)
      ? [...polozky].sort(
          (prva, druha) =>
            String(
              prva?.kluc || ""
            ).localeCompare(
              String(
                druha?.kluc || ""
              )
            )
        )
      : [];

  return JSON.stringify({
    riesenia:
      zoradenePolozky.map(
        (polozka) => ({
          kluc:
            polozka?.kluc || "",

          podiel: cislo(
            vahy?.[
              polozka?.kluc
            ]
          ),

          pocetFondov: cislo(
            polozka?.pocetFondov
          ),
        })
      ),

    mesacnyVklad: cislo(
      mesacnyVklad
    ),

    jednorazovyVklad: cislo(
      jednorazovyVklad
    ),
  });
}

function formatujProfilSkratky(produkt) {
  const profily = Array.isArray(
    produkt?.vhodnost?.profil
  )
    ? produkt.vhodnost.profil
    : [];

  return profily
    .map(
      (profil) =>
        PROFIL_SKratky[profil] || "—"
    )
    .join("/");
}

function vytvorProfilHint(produkt) {
  const profily = Array.isArray(
    produkt?.vhodnost?.profil
  )
    ? produkt.vhodnost.profil
    : [];

  return profily
    .map(
      (profil) =>
        PROFILY[profil] || profil
    )
    .join(", ");
}

function vytvorVstupPreOdporucaniaApi(
  vstup = {}
) {
  const vstupPreApi = {
    ...(vstup || {}),
  };

  delete vstupPreApi.vysledok;

  return vstupPreApi;
}

function useOdporucania(vstup, aktivne) {
  const [stav, setStav] = React.useState({
    data: null,
    nacitavaSa: false,
    chyba: "",
  });

  const vstupKluc = JSON.stringify(
    vytvorVstupPreOdporucaniaApi(vstup)
  );

  React.useEffect(() => {
    if (!aktivne) {
      setStav({
        data: null,
        nacitavaSa: false,
        chyba: "",
      });

      return undefined;
    }

    const controller = new AbortController();

    const casovac = window.setTimeout(async () => {
      setStav((povodnyStav) => ({
        ...povodnyStav,
        nacitavaSa: true,
        chyba: "",
      }));

      try {
        const odpoved = await fetch(
          apiUrl("/api/investicie/odporucania"),
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: vstupKluc,
            signal: controller.signal,
          }
        );

        const data = await odpoved
          .json()
          .catch(() => null);

        if (!odpoved.ok) {
          const textChyby =
            Array.isArray(data?.chyby) &&
            data.chyby.length > 0
              ? data.chyby
                  .map((polozka) => polozka.sprava)
                  .join(" ")
              : data?.message ||
                "Odporúčania sa nepodarilo načítať.";

          throw new Error(textChyby);
        }

        setStav({
          data,
          nacitavaSa: false,
          chyba: "",
        });
      } catch (e) {
        if (e?.name !== "AbortError") {
          setStav({
            data: null,
            nacitavaSa: false,
            chyba:
              e?.message ||
              "Odporúčania sa nepodarilo načítať.",
          });
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(casovac);
      controller.abort();
    };
  }, [aktivne, vstupKluc]);

  return stav;
}

function useAnalyzaVyberu(
  vstup,
  zvoleneProdukty,
  vahyRieseni,
  vlastnaVolba,
  ignorovatMinimalneInvesticie,
  aktivne
) {
  const [stav, setStav] =
    React.useState({
      data: null,
      nacitavaSa: false,
      chyba: "",
    });

  const vstupKluc = JSON.stringify({
    ...vytvorVstupPreOdporucaniaApi(
      vstup
    ),

    vybraneProdukty:
      zvoleneProdukty.map(
        (produkt) => ({
          kod: produkt.kod,
          podiel: cislo(
            vahyRieseni[
              vytvorKlucProduktu(
                produkt.kod
              )
            ]
          ),
        })
      ),

    vlastnaVolba,
    ignorovatMinimalneInvesticie:
      Boolean(
        ignorovatMinimalneInvesticie
      ),
  });

  React.useEffect(() => {
    if (!aktivne) {
      setStav({
        data: null,
        nacitavaSa: false,
        chyba: "",
      });

      return undefined;
    }

    const controller =
      new AbortController();

    const casovac = window.setTimeout(
      async () => {
        setStav(
          (povodnyStav) => ({
            ...povodnyStav,
            nacitavaSa: true,
            chyba: "",
          })
        );

        try {
          const odpoved = await fetch(
            apiUrl(
              "/api/investicie/odporucania/analyza-vyberu"
            ),
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type":
                  "application/json",
              },
              body: vstupKluc,
              signal:
                controller.signal,
            }
          );

          const data = await odpoved
            .json()
            .catch(() => null);

          if (!odpoved.ok) {
            const textChyby =
              Array.isArray(
                data?.chyby
              ) &&
              data.chyby.length > 0
                ? data.chyby
                    .map(
                      (polozka) =>
                        polozka.sprava
                    )
                    .join(" ")
                : data?.message ||
                  "Analýzu výberu sa nepodarilo načítať.";

            throw new Error(
              textChyby
            );
          }

          setStav({
            data,
            nacitavaSa: false,
            chyba: "",
          });
        } catch (e) {
          if (
            e?.name !==
            "AbortError"
          ) {
            setStav({
              data: null,
              nacitavaSa: false,
              chyba:
                e?.message ||
                "Analýzu výberu sa nepodarilo načítať.",
            });
          }
        }
      },
      200
    );

    return () => {
      window.clearTimeout(
        casovac
      );

      controller.abort();
    };
  }, [aktivne, vstupKluc]);

  return stav;
}

function ExternyOdkaz({
  odkaz,
  className,
  children,
  ariaLabel,
}) {
  if (!odkaz) {
    return (
      <span className={className}>
        {children || "—"}
      </span>
    );
  }

  return (
    <a
      className={className}
      href={odkaz}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

function ProduktRiadok({
  produkt,
  vybrany,
  poUprave = false,
  topProdukt = false,
  disabled = false,
  onVyber,
}) {
  const profilSkratky =
    formatujProfilSkratky(produkt);

  const profilHint =
    vytvorProfilHint(produkt);

  const maInfo =
    maInvesticneProduktInfo(
      produkt
    );

  const minInvestHorizont = Number(
    produkt?.vhodnost?.minInvestHorizont
  );

const maMinInvestHorizont =
    Number.isFinite(
      minInvestHorizont
    ) &&
    minInvestHorizont > 0;

  return (
    <div
      className={`invest-odporucania-table__row ${
        poUprave ? "is-adjustment" : ""
      } ${topProdukt ? "is-top" : ""}`}
    >
      <div className="invest-odporucania-table__select">
        <input
          type="checkbox"
          checked={vybrany}
          disabled={poUprave || disabled}
          onChange={() => onVyber(produkt)}
          aria-label={
            produkt.rezimVyberu ===
            "vlastnaVolba"
              ? `Otvoriť vlastné riešenie: ${
                  produkt.nazovProduktu?.nazov ||
                  produkt.kod
                }`
              : `Vybrať produkt: ${
                  produkt.nazovProduktu?.nazov ||
                  produkt.kod
                }`
          }
        />
      </div>

      <ExternyOdkaz
        odkaz={produkt.spolocnost?.odkaz}
        className="invest-odporucania-table__link invest-odporucania-table__company"
        ariaLabel={`Otvoriť stránku spoločnosti ${
          produkt.spolocnost?.nazov || ""
        }`}
      >
        {produkt.spolocnost?.nazov || "—"}
      </ExternyOdkaz>

      <ExternyOdkaz
        odkaz={produkt.nazovProduktu?.odkaz}
        className="invest-odporucania-table__link invest-odporucania-table__product"
        ariaLabel={`Otvoriť produkt ${
          produkt.nazovProduktu?.nazov || ""
        }`}
      >
        {produkt.nazovProduktu?.nazov || "—"}
      </ExternyOdkaz>

      <div className="invest-odporucania-table__center">
        <HintPopover
          obsah={profilHint}
          ariaLabel={`Profil: ${profilHint}`}
          pozicia="top"
          triggerMode="hover"
          triggerClassName="invest-odporucania-profile-trigger"
          panelClassName="invest-odporucania-hint-panel"
        >
          <span>
            {profilSkratky || "—"}
          </span>
        </HintPopover>
      </div>

      <div className="invest-odporucania-table__center">
        {produkt.SRI ?? "—"}
      </div>

      <div className="invest-odporucania-table__center">
        {maMinInvestHorizont
          ? `${minInvestHorizont}+`
          : "—"}
      </div>

      <div className="invest-odporucania-table__icon-cell">
        <HintPopover
          obsah={
            <InvesticnePoplatkyPopoverObsah
              produkt={produkt}
            />
          }
          ariaLabel={`Poplatky produktu ${
            produkt.nazovProduktu?.nazov || ""
          }`}
          pozicia="top"
          triggerMode="click"
          triggerClassName="invest-odporucania-icon-button"
          panelClassName="invest-odporucania-hint-panel invest-odporucania-poplatky-panel"
        >
          <img
            src={poplatkyIkona}
            alt=""
            className="invest-odporucania-table__icon"
          />
        </HintPopover>
      </div>

      <div className="invest-odporucania-table__icon-cell">
        {maInfo ? (
          <HintPopover
            obsah={
              <InvesticneInfoPopoverObsah
                produkt={produkt}
              />
            }
            ariaLabel={`Informácie o produkte ${
              produkt.nazovProduktu
                ?.nazov || ""
            }`}
            pozicia="top"
            triggerMode="click"
            triggerClassName="invest-odporucania-info-link"
            panelClassName="invest-odporucania-hint-panel invest-odporucania-info-panel"
          >
            <img
              src={infoCircleIkona}
              alt=""
              className="invest-odporucania-table__icon"
            />
          </HintPopover>
        ) : (
          <span className="invest-odporucania-table__empty">
            —
          </span>
        )}
      </div>
    </div>
  );
}

function ModalVah({
  modal,
  onZmena,
  onZmenaIgnorovania,
  onZrusit,
  onPotvrdit,
}) {
  const prvyInputRef =
    React.useRef(null);

  const onZrusitRef =
    React.useRef(onZrusit);

  const modalJeOtvoreny =
    Boolean(modal);

  React.useEffect(() => {
    onZrusitRef.current =
      onZrusit;
  }, [onZrusit]);

  React.useEffect(() => {
    if (!modalJeOtvoreny) {
      return undefined;
    }

    const povodnyOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    prvyInputRef.current?.focus();

    const priKlavesnici = (e) => {
      if (e.key === "Escape") {
        onZrusitRef.current();
      }
    };

    document.addEventListener(
      "keydown",
      priKlavesnici
    );

    return () => {
      document.body.style.overflow =
        povodnyOverflow;

      document.removeEventListener(
        "keydown",
        priKlavesnici
      );
    };
  }, [modalJeOtvoreny]);

  if (!modal) {
    return null;
  }

  const sucet = modal.polozky.reduce(
    (spolu, polozka) =>
      spolu +
      cislo(
        modal.vahy[polozka.kluc]
      ),
    0
  );

  const vahySuPlatne =
    modal.polozky.every(
      (polozka) => {
        const vaha = cislo(
          modal.vahy[
            polozka.kluc
          ]
        );

        return (
          vaha > 0 &&
          vaha <= 100
        );
      }
    ) &&
    Math.abs(sucet - 100) <
      0.001;

  const maUpozornenieMinimalnychInvesticii =
    modal
      .vyhodnotenieMinimalnychInvesticii
      ?.platne === false;

  return (
    <div
      className="pdf-modal-overlay"
      onMouseDown={(e) => {
        if (
          e.target ===
          e.currentTarget
        ) {
          onZrusit();
        }
      }}
    >
      <div
        className="pdf-modal invest-vahy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invest-vahy-modal-title"
        onMouseDown={(e) =>
          e.stopPropagation()
        }
      >
        <div className="pdf-modal__header">
          <h3
            id="invest-vahy-modal-title"
            className="pdf-modal__title"
          >
            Nastavenie váh produktov
          </h3>
        </div>

        <div className="pdf-modal__body">
          <div className="invest-vahy-modal__zoznam">
            {modal.polozky.map(
              (
                polozka,
                index
              ) => (
                <label
                  key={
                    polozka.kluc
                  }
                  className="invest-vahy-modal__riadok"
                >
                  <span>
                    {polozka.spolocnost && (
                      <small>
                        {
                          polozka.spolocnost
                        }
                      </small>
                    )}

                    <strong>
                      {
                        polozka.nazov
                      }
                    </strong>
                  </span>

                  <span className="invest-vahy-modal__input-wrap">
                    <input
                      ref={
                        index === 0
                          ? prvyInputRef
                          : undefined
                      }
                      className="pdf-modal__input"
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      value={
                        modal.vahy[
                          polozka
                            .kluc
                        ]
                      }
                      onChange={(
                        e
                      ) =>
                        onZmena(
                          polozka.kluc,
                          e.target
                            .value
                        )
                      }
                    />

                    <b>%</b>
                  </span>
                </label>
              )
            )}
          </div>

          <div
            className={
              vahySuPlatne
                ? "invest-vahy-modal__sucet is-valid"
                : "invest-vahy-modal__sucet is-error"
            }
          >
            Súčet váh:{" "}
            {sucet.toLocaleString(
              "sk-SK",
              {
                maximumFractionDigits: 2,
              }
            )}{" "}
            %
          </div>

          {maUpozornenieMinimalnychInvesticii && (
            <div
              id="invest-vahy-modal-minima"
              className="invest-vahy-modal__chyba"
              role="alert"
            >
              <strong>
                {
                  modal
                    .vyhodnotenieMinimalnychInvesticii
                    .hlavnaSprava
                }
              </strong>

              <ul>
                {modal
                  .vyhodnotenieMinimalnychInvesticii
                  .spravy.map(
                    (sprava) => (
                      <li key={sprava}>
                        {sprava}
                      </li>
                    )
                  )}
              </ul>

              <label className="pdf-modal__checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(
                    modal
                      .ignorovatMinimalneInvesticie
                  )}
                  aria-describedby="invest-vahy-modal-minima"
                  onChange={(e) =>
                    onZmenaIgnorovania(
                      e.target.checked
                    )
                  }
                />

                <span>
                  Rozumiem upozorneniu a chcem
                  pokračovať aj bez splnenia
                  minimálnych súm.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="pdf-modal__footer">
          <button
            type="button"
            className="pdf-modal__btn pdf-modal__btn--secondary"
            onClick={onZrusit}
          >
            Zrušiť
          </button>

          <button
            type="button"
            className="pdf-modal__btn pdf-modal__btn--primary"
            disabled={
              !vahySuPlatne ||
              (
                maUpozornenieMinimalnychInvesticii &&
                !modal
                  .ignorovatMinimalneInvesticie
              )
            }
            onClick={onPotvrdit}
          >
            {maUpozornenieMinimalnychInvesticii &&
            modal
              .ignorovatMinimalneInvesticie
              ? "Pokračovať napriek upozorneniu"
              : "Potvrdiť váhy"}
          </button>
        </div>
      </div>
    </div>
  );
}

const InvesticneOdporucaniaVstupKarta =
  React.forwardRef(
    function InvesticneOdporucaniaVstupKarta(
      {
        porovnanieInvesticii,
        aktivnyInvesticnyVariant,
        variant1,
        variant2,
      },
      ref
    ) {
  const odporucania1 = useOdporucania(
    variant1,
    true
  );

  const odporucania2 = useOdporucania(
    variant2,
    porovnanieInvesticii
  );

  const [rezimy, setRezimy] = React.useState({
    variant1: "odporucania",
    variant2: "odporucania",
  });

  const [vybraneKody, setVybraneKody] =
    React.useState({
      variant1: [],
      variant2: [],
    });

  const [
    vlastnaVolbaProdukty,
    setVlastnaVolbaProdukty,
  ] = React.useState({
    variant1: null,
    variant2: null,
  });

  const [vlastneVolby, setVlastneVolby] =
    React.useState({
      variant1: vytvorVlastnuVolbu(),
      variant2: vytvorVlastnuVolbu(),
    });

    const [vahyRieseni, setVahyRieseni] =
      React.useState({
        variant1: {},
        variant2: {},
      });

    const [modalVah, setModalVah] =
      React.useState(null);

    const [
      ignorovaneNastaveniaMinimalnychInvesticii,
      setIgnorovaneNastaveniaMinimalnychInvesticii,
    ] = React.useState({
      variant1: "",
      variant2: "",
    });
  const [chybaVyberu, setChybaVyberu] =
    React.useState("");
  const klucVstupovMinimalnychInvesticiiVariant1 =
    JSON.stringify([
      cislo(
        variant1?.mesacnyVklad
      ),
      cislo(
        variant1?.jednorazovyVklad
      ),
    ]);

  const klucVstupovMinimalnychInvesticiiVariant2 =
    JSON.stringify([
      cislo(
        variant2?.mesacnyVklad
      ),
      cislo(
        variant2?.jednorazovyVklad
      ),
    ]);

  React.useEffect(() => {
    setIgnorovaneNastaveniaMinimalnychInvesticii(
      (povodneNastavenia) => {
        if (
          !povodneNastavenia
            .variant1
        ) {
          return povodneNastavenia;
        }

        return {
          ...povodneNastavenia,
          variant1: "",
        };
      }
    );
  }, [
    klucVstupovMinimalnychInvesticiiVariant1,
  ]);

  React.useEffect(() => {
    setIgnorovaneNastaveniaMinimalnychInvesticii(
      (povodneNastavenia) => {
        if (
          !povodneNastavenia
            .variant2
        ) {
          return povodneNastavenia;
        }

        return {
          ...povodneNastavenia,
          variant2: "",
        };
      }
    );
  }, [
    klucVstupovMinimalnychInvesticiiVariant2,
  ]);

  const variantKluc =
    porovnanieInvesticii &&
    aktivnyInvesticnyVariant === 2
      ? "variant2"
      : "variant1";

  const stav =
    variantKluc === "variant2"
      ? odporucania2
      : odporucania1;

  const odporucene = Array.isArray(
    stav.data?.odporucene
  )
    ? stav.data.odporucene
    : [];

  const poUprave = Array.isArray(
    stav.data?.vhodnePoUprave
  )
    ? stav.data.vhodnePoUprave
    : [];

  const najblizsiProduktPoUprave =
    poUprave.find(
      (produkt) =>
        Array.isArray(
          produkt?.potrebneUpravy
        ) &&
        produkt.potrebneUpravy.length ===
          1
    ) || null;

  const rezim = rezimy[variantKluc];
  const riadky = vlastneVolby[variantKluc];
  const vybrane = vybraneKody[variantKluc];

  const vlastnaVolbaProdukt =
    vlastnaVolbaProdukty[variantKluc];

  React.useEffect(() => {
    const dataPodlaVariantu = {
      variant1: odporucania1.data,
      variant2: odporucania2.data,
    };

    setVybraneKody((povodneVybery) => {
      const vysledok = {
        ...povodneVybery,
      };

      Object.entries(dataPodlaVariantu).forEach(
        ([kluc, data]) => {
          const dostupne = new Set(
            (data?.odporucene || [])
              .filter(
                (produkt) =>
                  produkt.rezimVyberu === "produkt"
              )
              .map((produkt) => produkt.kod)
          );

          vysledok[kluc] =
            povodneVybery[kluc].filter(
              (kod) => dostupne.has(kod)
            );
        }
      );

      return vysledok;
    });

    setVlastnaVolbaProdukty(
      (povodneProdukty) => {
        const vysledok = {
          ...povodneProdukty,
        };

        Object.entries(dataPodlaVariantu).forEach(
          ([kluc, data]) => {
            const povodnyKod =
              povodneProdukty[kluc]?.kod;

            const dostupny = (
              data?.odporucene || []
            ).some(
              (produkt) =>
                produkt.rezimVyberu ===
                  "vlastnaVolba" &&
                produkt.kod === povodnyKod
            );

            if (!dostupny) {
              vysledok[kluc] = null;
            }
          }
        );

        return vysledok;
      }
    );
  }, [odporucania1.data, odporucania2.data]);

  const vytvorPolozkyVah = (
    kody,
    produktVlastnejVolby,
    novyRezim,
    pocetFondovVlastnejVolby = 0
  ) => {
  const polozkyProduktov = kody
    .map((kod) =>
      odporucene.find(
        (produkt) =>
          produkt.kod === kod
      )
    )
    .filter(Boolean)
    .map((produkt) => ({
      kluc: vytvorKlucProduktu(
        produkt.kod
      ),
      spolocnost:
        produkt.spolocnost?.nazov ||
        "",
      nazov:
        produkt.nazovProduktu
          ?.nazov ||
        produkt.kod,

      minimalnaInvesticia: {
        ...produkt.vhodnost
          ?.minimalnaInvesticia,
      },

      pocetFondov: 0,
    }));

  const zahrnutVlastnuVolbu =
    Boolean(
      produktVlastnejVolby
    ) ||
    novyRezim ===
      "vlastnaVolba";

  if (!zahrnutVlastnuVolbu) {
    return polozkyProduktov;
  }

  return [
    ...polozkyProduktov,
    {
      kluc: VLASTNA_VOLBA_KLUC,
      spolocnost:
        produktVlastnejVolby
          ?.spolocnost?.nazov ||
        "",
      nazov:
        produktVlastnejVolby
          ?.nazovProduktu?.nazov ||
        "Vlastný výber fondov",

      minimalnaInvesticia: {
        ...produktVlastnejVolby
          ?.vhodnost
          ?.minimalnaInvesticia,
      },

      pocetFondov:
        pocetFondovVlastnejVolby,
    },
  ];
};

const aplikujNovyVyber = ({
  kody,
  produktVlastnejVolby,
  novyRezim,
}) => {
  const polozky =
    vytvorPolozkyVah(
      kody,
      produktVlastnejVolby,
      novyRezim,
      zadaneRiadky.length
    );

  if (
    polozky.length >
    MAX_POCET_VYBRANYCH_RIESENI
  ) {
    setChybaVyberu(
      `Vybrať je možné najviac ${MAX_POCET_VYBRANYCH_RIESENI} investičné riešenia.`
    );

    return;
  }

  setChybaVyberu("");

  const noveVahy =
    vytvorRovnomerneVahy(
      polozky
    );

  const povodnyStav = {
    kody: [...vybrane],

    produktVlastnejVolby:
      vlastnaVolbaProdukt,

    rezim,

    vahy: {
      ...vahyRieseni[
        variantKluc
      ],
    },
  };

  setVybraneKody(
    (povodneVybery) => ({
      ...povodneVybery,
      [variantKluc]: kody,
    })
  );

  setVlastnaVolbaProdukty(
    (povodneProdukty) => ({
      ...povodneProdukty,
      [variantKluc]:
        produktVlastnejVolby,
    })
  );

  setRezimy(
    (povodneRezimy) => ({
      ...povodneRezimy,
      [variantKluc]:
        novyRezim,
    })
  );

  setVahyRieseni(
    (povodneVahy) => ({
      ...povodneVahy,
      [variantKluc]:
        noveVahy,
    })
  );

  if (polozky.length >= 2) {
    setModalVah({
      variantKluc,
      polozky,
      vahy: noveVahy,
      povodnyStav,
      ignorovatMinimalneInvesticie:
        false,
      vyhodnotenieMinimalnychInvesticii:
        null,
    });
  }
};

const zmenRezimVlastnejVolby = (
  zapnuty
) => {
  aplikujNovyVyber({
    kody: vybrane,

    produktVlastnejVolby:
      zapnuty
        ? vlastnaVolbaProdukt
        : null,

    novyRezim: zapnuty
      ? "vlastnaVolba"
      : "odporucania",
  });
};

const vyberProdukt = (produkt) => {
  if (
    produkt.rezimVyberu ===
    "vlastnaVolba"
  ) {
    const jeAktualneVybrany =
      vlastnaVolbaProdukt?.kod ===
      produkt.kod;

    aplikujNovyVyber({
      kody: vybrane,

      produktVlastnejVolby:
        jeAktualneVybrany
          ? null
          : produkt,

      novyRezim:
        jeAktualneVybrany
          ? "odporucania"
          : "vlastnaVolba",
    });

    return;
  }

  const noveKody =
    vybrane.includes(produkt.kod)
      ? vybrane.filter(
          (kod) =>
            kod !== produkt.kod
        )
      : [
          ...vybrane,
          produkt.kod,
        ];

  aplikujNovyVyber({
    kody: noveKody,

    produktVlastnejVolby:
      vlastnaVolbaProdukt,

    novyRezim: rezim,
  });
};

const zmenModalVahu = (
  kluc,
  hodnota
) => {
  setModalVah(
    (povodnyModal) => ({
      ...povodnyModal,

      vahy: {
        ...povodnyModal.vahy,
        [kluc]: hodnota,
      },

      ignorovatMinimalneInvesticie:
        false,

      vyhodnotenieMinimalnychInvesticii:
        null,
    })
  );
};

const zmenIgnorovanieMinimalnychInvesticii = (
  ignorovat
) => {
  setModalVah(
    (povodnyModal) => ({
      ...povodnyModal,

      ignorovatMinimalneInvesticie:
        ignorovat,
    })
  );
};

const zrusModalVah = () => {
  if (modalVah?.povodnyStav) {
    const {
      kody,
      produktVlastnejVolby,
      rezim: povodnyRezim,
      vahy,
    } = modalVah.povodnyStav;

    setVybraneKody(
      (povodneVybery) => ({
        ...povodneVybery,
        [modalVah.variantKluc]:
          kody,
      })
    );

    setVlastnaVolbaProdukty(
      (povodneProdukty) => ({
        ...povodneProdukty,

        [modalVah.variantKluc]:
          produktVlastnejVolby,
      })
    );

    setRezimy(
      (povodneRezimy) => ({
        ...povodneRezimy,

        [modalVah.variantKluc]:
          povodnyRezim,
      })
    );

    setVahyRieseni(
      (povodneVahy) => ({
        ...povodneVahy,

        [modalVah.variantKluc]:
          vahy,
      })
    );
  }

  setModalVah(null);
};

const potvrdModalVah = () => {
  const variantPreModal =
    modalVah.variantKluc ===
      "variant2"
      ? variant2
      : variant1;

  const vyhodnotenieMinimalnychInvesticii =
    vyhodnotMinimalneInvesticieVah({
      polozky: modalVah.polozky,
      vahy: modalVah.vahy,

      mesacnyVklad:
        variantPreModal
          ?.mesacnyVklad,

      jednorazovyVklad:
        variantPreModal
          ?.jednorazovyVklad,
    });

  if (
    !vyhodnotenieMinimalnychInvesticii
      .platne &&
    !modalVah
      .ignorovatMinimalneInvesticie
  ) {
    setModalVah(
      (povodnyModal) => ({
        ...povodnyModal,

        vyhodnotenieMinimalnychInvesticii,
      })
    );

    return;
  }

  const potvrdeneVahy =
    Object.entries(
      modalVah.vahy
    ).reduce(
      (
        vahy,
        [kluc, hodnota]
      ) => ({
        ...vahy,
        [kluc]: cislo(hodnota),
      }),
      {}
    );

  const ignorovanieMinimalnychInvesticiiJePotvrdene =
    !vyhodnotenieMinimalnychInvesticii
      .platne &&
    Boolean(
      modalVah
        .ignorovatMinimalneInvesticie
    );

  const klucPotvrdenehoIgnorovania =
    ignorovanieMinimalnychInvesticiiJePotvrdene
      ? vytvorKlucIgnorovaniaMinimalnychInvesticii({
          polozky:
            modalVah.polozky,

          vahy:
            potvrdeneVahy,

          mesacnyVklad:
            variantPreModal
              ?.mesacnyVklad,

          jednorazovyVklad:
            variantPreModal
              ?.jednorazovyVklad,
        })
      : "";

  setIgnorovaneNastaveniaMinimalnychInvesticii(
    (povodneNastavenia) => ({
      ...povodneNastavenia,

      [modalVah.variantKluc]:
        klucPotvrdenehoIgnorovania,
    })
  );

  setVahyRieseni(
    (povodneVahy) => ({
      ...povodneVahy,

      [modalVah.variantKluc]:
        potvrdeneVahy,
    })
  );

  setModalVah(null);
};

  const upravRiadok = (id, zmeny) => {
    setVlastneVolby((povodneVolby) => ({
      ...povodneVolby,

      [variantKluc]: povodneVolby[
        variantKluc
      ].map((riadok) =>
        riadok.id === id
          ? {
              ...riadok,
              ...zmeny,
            }
          : riadok
      ),
    }));
  };

  const pridajRiadok = () => {
    setVlastneVolby((povodneVolby) => {
      const povodneRiadky =
        povodneVolby[variantKluc];

      if (povodneRiadky.length >= 8) {
        return povodneVolby;
      }

      const id =
        Math.max(
          ...povodneRiadky.map(
            (riadok) => riadok.id
          )
        ) + 1;

      return {
        ...povodneVolby,

        [variantKluc]: [
          ...povodneRiadky,
          {
            id,
            nazov: "",
            podiel: 0,
            triedaAktiv: "",
            priemerneZhodnotenie: "",
          },
        ],
      };
    });
  };

  const odstranRiadok = (id) => {
    if (riadky.length <= 3) return;

    setVlastneVolby((povodneVolby) => ({
      ...povodneVolby,

      [variantKluc]: povodneVolby[
        variantKluc
      ].filter((riadok) => riadok.id !== id),
    }));
  };

  const zadaneRiadky = riadky.filter(
    (riadok) =>
      riadok.nazov.trim() ||
      cislo(riadok.podiel) > 0 ||
      riadok.triedaAktiv ||
      riadok.priemerneZhodnotenie !== ""
  );

  const sucetPodielov = zadaneRiadky.reduce(
    (spolu, riadok) =>
      spolu + cislo(riadok.podiel),
    0
  );

  const vlastnaVolbaPlatna =
    zadaneRiadky.length > 0 &&
    zadaneRiadky.every(
      (riadok) =>
        riadok.nazov.trim() &&
        cislo(riadok.podiel) > 0 &&
        riadok.triedaAktiv
    ) &&
    Math.abs(sucetPodielov - 100) < 0.001;

  const zvoleneProdukty = vybrane
  .map((kod) =>
    odporucene.find(
      (produkt) =>
        produkt.kod === kod
    )
  )
  .filter(Boolean);

const vlastnaVolbaJeVybrana =
  Boolean(
    vlastnaVolbaProdukt
  ) ||
  rezim === "vlastnaVolba";

const pocetVybranychRieseni =
  vybrane.length +
  (
    vlastnaVolbaJeVybrana
      ? 1
      : 0
  );

  React.useEffect(() => {
  if (
    pocetVybranychRieseni >= 2
  ) {
    return;
  }

  setIgnorovaneNastaveniaMinimalnychInvesticii(
    (povodneNastavenia) => {
      if (
        !povodneNastavenia[
          variantKluc
        ]
      ) {
        return povodneNastavenia;
      }

      return {
        ...povodneNastavenia,
        [variantKluc]: "",
      };
    }
  );
}, [
  variantKluc,
  pocetVybranychRieseni,
]);

const maProduktovyVyber =
  zvoleneProdukty.length > 0;

const maVlastnuVolbu =
  vlastnaVolbaJeVybrana &&
  vlastnaVolbaPlatna;

const aktivneVahy =
  vahyRieseni[variantKluc];

const kluceAktivnychRieseni = [
  ...zvoleneProdukty.map(
    (produkt) =>
      vytvorKlucProduktu(
        produkt.kod
      )
  ),

  ...(
    vlastnaVolbaJeVybrana
      ? [VLASTNA_VOLBA_KLUC]
      : []
  ),
];

const klucAktivnehoVyberu =
  kluceAktivnychRieseni.join(
    "|"
  );

React.useEffect(() => {
  setVahyRieseni(
    (povodneVahy) => {
      const noveAktivneKluce =
        klucAktivnehoVyberu
          ? klucAktivnehoVyberu.split(
              "|"
            )
          : [];

      const povodneKluce =
        Object.keys(
          povodneVahy[
            variantKluc
          ]
        ).sort();

      const noveKluce = [
        ...noveAktivneKluce,
      ].sort();

      if (
        povodneKluce.length ===
          noveKluce.length &&
        povodneKluce.every(
          (kluc, index) =>
            kluc ===
            noveKluce[index]
        )
      ) {
        return povodneVahy;
      }

      return {
        ...povodneVahy,

        [variantKluc]:
          vytvorRovnomerneVahy(
            noveAktivneKluce.map(
              (kluc) => ({
                kluc,
              })
            )
          ),
      };
    }
  );
}, [
  variantKluc,
  klucAktivnehoVyberu,
]);

const sucetAktivnychVah =
  kluceAktivnychRieseni.reduce(
    (spolu, kluc) =>
      spolu +
      cislo(
        aktivneVahy[kluc]
      ),
    0
  );

const vahyVyberuSuPlatne =
  kluceAktivnychRieseni.length >
    0 &&
  kluceAktivnychRieseni.every(
    (kluc) =>
      cislo(
        aktivneVahy[kluc]
      ) > 0
  ) &&
  Math.abs(
    sucetAktivnychVah - 100
  ) < 0.001;

const vlastnaVolbaPreAnalyzu =
  maVlastnuVolbu
    ? {
        kod:
          vlastnaVolbaProdukt
            ?.kod,

        pocetFondov:
          zadaneRiadky.length,

        podiel: cislo(
          aktivneVahy[
            VLASTNA_VOLBA_KLUC
          ]
        ),

        zlozenieAktiv:
          vytvorZlozenieVlastnejVolby(
            zadaneRiadky
          ),
      }
    : null;

const aktivnyVariant =
  variantKluc === "variant2"
    ? variant2
    : variant1;

const polozkyAktivnehoVyberu =
  vytvorPolozkyVah(
    vybrane,
    vlastnaVolbaProdukt,
    rezim,
    zadaneRiadky.length
  );

const klucAktualnehoNastaveniaMinimalnychInvesticii =
  vytvorKlucIgnorovaniaMinimalnychInvesticii({
    polozky:
      polozkyAktivnehoVyberu,

    vahy:
      aktivneVahy,

    mesacnyVklad:
      aktivnyVariant
        ?.mesacnyVklad,

    jednorazovyVklad:
      aktivnyVariant
        ?.jednorazovyVklad,
  });

const ignorovatMinimalneInvesticiePreAnalyzu =
  pocetVybranychRieseni >= 2 &&
  Boolean(
    ignorovaneNastaveniaMinimalnychInvesticii[
      variantKluc
    ]
  ) &&
  ignorovaneNastaveniaMinimalnychInvesticii[
    variantKluc
  ] ===
    klucAktualnehoNastaveniaMinimalnychInvesticii;

const analyzaVyberu =
  useAnalyzaVyberu(
    aktivnyVariant,
    zvoleneProdukty,
    aktivneVahy,
    vlastnaVolbaPreAnalyzu,
    ignorovatMinimalneInvesticiePreAnalyzu,

    (
      maProduktovyVyber ||
      maVlastnuVolbu
    ) &&
      vahyVyberuSuPlatne &&
      (
        !vlastnaVolbaJeVybrana ||
        maVlastnuVolbu
      )
  );

React.useImperativeHandle(
  ref,
  () => ({
    ziskajPdfOdporucania: () =>
      vytvorInvestPdfOdporucania({
        variantKluc,
        aktivnyVariant,
        zvoleneProdukty,
        maProduktovyVyber,
        maVlastnuVolbu,
        vlastnaVolbaJeVybrana,
        vlastnaVolbaProdukt,
        zadaneRiadky,
        aktivneVahy,
        vahyVyberuSuPlatne,
        analyzaVyberu,
      }),
  }),
  [
    variantKluc,
    aktivnyVariant,
    zvoleneProdukty,
    maProduktovyVyber,
    maVlastnuVolbu,
    vlastnaVolbaJeVybrana,
    vlastnaVolbaProdukt,
    zadaneRiadky,
    aktivneVahy,
    vahyVyberuSuPlatne,
    analyzaVyberu,
  ]
);

const otvorUpravuVah = () => {
  const polozky =
    vytvorPolozkyVah(
      vybrane,
      vlastnaVolbaProdukt,
      rezim,
      zadaneRiadky.length
    );

  if (polozky.length < 2) {
    return;
  }

  setModalVah({
    variantKluc,
    polozky,

    vahy: {
      ...aktivneVahy,
    },

    povodnyStav: null,

    ignorovatMinimalneInvesticie:
      false,

    vyhodnotenieMinimalnychInvesticii:
      null,
  });
};

  return (
    <>
      <div className="card-section card-section--invest-odporucania card-section--invest-odporucania-vstup">
        <div className="invest-odporucania-head">
            <h2>Odporúčania</h2>

            <div className="toggle-group invest-scenare-compare-toggle">
            <label className="chk">
                <strong>Vlastné riešenie</strong>

                <input
                type="checkbox"
                checked={
                    rezim === "vlastnaVolba"
                }
                onChange={(e) =>
                  zmenRezimVlastnejVolby(
                    e.target.checked
                  )
                }
                />
            </label>
            </div>
        </div>

        {rezim === "odporucania" ? (
            <div className="invest-odporucania-table-scroll">
                <div className="invest-odporucania-table">
                <div className="invest-odporucania-table__header">
                  <div
                    className="invest-odporucania-table__select"
                    aria-hidden="true"
                  />

                  <div className="invest-odporucania-table__header-company">
                    Spoločnosť
                  </div>

                  <div>Produkt</div>

                  <div className="invest-odporucania-table__center">
                    Profil
                  </div>

                  <div className="invest-odporucania-table__center">
                    <HintPopover
                      obsah="Súhrnný ukazovateľ rizika"
                      ariaLabel="Súhrnný ukazovateľ rizika"
                      pozicia="top"
                      triggerMode="hover"
                      triggerClassName="invest-odporucania-header-trigger"
                      panelClassName="invest-odporucania-hint-panel"
                    >
                      <span>SRI</span>
                    </HintPopover>
                  </div>

                  <div className="invest-odporucania-table__center">
                    <HintPopover
                      obsah="Minimálny investičný horizont"
                      ariaLabel="Minimálny investičný horizont"
                      pozicia="top"
                      triggerMode="hover"
                      triggerClassName="invest-odporucania-header-trigger"
                      panelClassName="invest-odporucania-hint-panel"
                    >
                      <span>H.</span>
                    </HintPopover>
                  </div>

                  <div className="invest-odporucania-table__center">
                    <HintPopover
                      obsah="Poplatky"
                      ariaLabel="Poplatky"
                      pozicia="top"
                      triggerMode="hover"
                      triggerClassName="invest-odporucania-header-trigger"
                      panelClassName="invest-odporucania-hint-panel"
                    >
                      <span>P.</span>
                    </HintPopover>
                  </div>

                  <div className="invest-odporucania-table__center">
                    Info
                  </div>
                </div>

                {stav.nacitavaSa && !stav.data && (
                    <div className="invest-odporucania-table__message">
                    Načítavam odporúčania…
                    </div>
                )}

                {stav.chyba && (
                    <div className="invest-odporucania-table__message is-error">
                    {stav.chyba}
                    </div>
                )}

                {chybaVyberu && (
                  <div className="invest-odporucania-table__message is-error">
                    {chybaVyberu}
                  </div>
                )}

                {!stav.chyba &&
                  !stav.nacitavaSa &&
                  odporucene.length === 0 &&
                  !najblizsiProduktPoUprave && (
                    <div className="invest-odporucania-table__message">
                      Pre aktuálne vstupy nie je
                      pripravený produktový návrh.
                    </div>
                  )}

                {odporucene.map((produkt, index) => (
                    <ProduktRiadok
                    key={produkt.kod}
                    produkt={produkt}
                    topProdukt={
                      index === 0 &&
                      odporucene.length +
                        (
                          najblizsiProduktPoUprave
                            ? 1
                            : 0
                        ) >
                        1
                    }
                    vybrany={
                        produkt.rezimVyberu ===
                        "vlastnaVolba"
                        ? vlastnaVolbaProdukt?.kod ===
                            produkt.kod
                        : vybrane.includes(
                            produkt.kod
                            )
                    }
                    disabled={
                      pocetVybranychRieseni >=
                        MAX_POCET_VYBRANYCH_RIESENI &&
                      !(
                        produkt.rezimVyberu ===
                        "vlastnaVolba"
                          ? vlastnaVolbaProdukt?.kod ===
                            produkt.kod
                          : vybrane.includes(
                              produkt.kod
                            )
                      )
                    }
                    onVyber={vyberProdukt}
                    />
                ))}

                {najblizsiProduktPoUprave && (
                  <ProduktRiadok
                    key={
                      najblizsiProduktPoUprave.kod
                    }
                    produkt={
                      najblizsiProduktPoUprave
                    }
                    vybrany={false}
                    poUprave
                    onVyber={vyberProdukt}
                  />
                )}
                </div>
            </div>
            ) : (
          <div className="invest-vlastna-volba">
            {vlastnaVolbaProdukt && (
              <div className="invest-vlastna-volba__produkt">
                <span>Zvolený produkt:</span>

                <strong>
                  {
                    vlastnaVolbaProdukt
                      .nazovProduktu?.nazov
                  }
                </strong>

                <button
                  type="button"
                  onClick={() =>
                    setVlastnaVolbaProdukty(
                      (povodneProdukty) => ({
                        ...povodneProdukty,
                        [variantKluc]: null,
                      })
                    )
                  }
                  aria-label="Odstrániť zvolený rámec"
                >
                  ×
                </button>
              </div>
            )}

            <div className="invest-vlastna-volba__scroll">
              {riadky.map((riadok, index) => (
                <div
                  key={riadok.id}
                  className="invest-vlastna-volba__riadok"
                >
                  <div className="form form--m">
                    <label className="form_label">
                      Fond / stratégia
                    </label>

                    <input
                      className="form_input"
                      value={riadok.nazov}
                      maxLength={120}
                      placeholder={`Voľba ${
                        index + 1
                      }`}
                      onChange={(e) =>
                        upravRiadok(
                          riadok.id,
                          {
                            nazov:
                              e.target.value,
                          }
                        )
                      }
                    />
                  </div>

                  <div className="form form--m">
                    <label className="form_label">
                      Podiel
                    </label>

                    <PercentaVstupy
                      value={riadok.podiel}
                      onChange={(v) =>
                        upravRiadok(
                          riadok.id,
                          {
                            podiel: Math.min(
                              100,
                              Math.max(
                                0,
                                Math.round(
                                  (v + Number.EPSILON) *
                                    10
                                ) / 10
                              )
                            ),
                          }
                        )
                      }
                      maxInt={3}
                      decimalPlaces={1}
                      placeholder="0"
                    />
                  </div>

                  <div className="form form--m">
                    <label className="form_label">
                      Trieda aktív
                    </label>

                    <select
                      className="form_select"
                      value={riadok.triedaAktiv}
                      onChange={(e) =>
                        upravRiadok(
                          riadok.id,
                          {
                            triedaAktiv:
                              e.target.value,
                          }
                        )
                      }
                    >
                      <option value="">
                        Vyberte
                      </option>

                      {TRIEDY_AKTIV.map(
                        ([hodnota, nazov]) => (
                          <option
                            key={hodnota}
                            value={hodnota}
                          >
                            {nazov}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div className="form form--m">
                    <label className="form_label">
                      Zhodnotenie p.a.
                    </label>

                    <PercentaVstupy
                      value={
                        riadok.priemerneZhodnotenie
                      }
                      onChange={(v) =>
                        upravRiadok(
                          riadok.id,
                          {
                            priemerneZhodnotenie:
                              Math.min(
                                99,
                                Math.max(0, v)
                              ),
                          }
                        )
                      }
                      maxInt={2}
                      placeholder="—"
                    />
                  </div>

                  <button
                    type="button"
                    className="invest-vlastna-volba__odstranit"
                    disabled={riadky.length <= 3}
                    onClick={() =>
                      odstranRiadok(riadok.id)
                    }
                    aria-label={`Odstrániť riadok ${
                      index + 1
                    }`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="invest-vlastna-volba__footer">
              <button
                type="button"
                className="action-btn"
                disabled={riadky.length >= 8}
                onClick={pridajRiadok}
              >
                + Pridať riadok
              </button>

              <div
                className={`invest-vlastna-volba__sucet ${
                  zadaneRiadky.length > 0 &&
                  !vlastnaVolbaPlatna
                    ? "is-error"
                    : ""
                }`}
              >
                Súčet podielov:{" "}
                <strong>
                  {sucetPodielov.toLocaleString(
                    "sk-SK"
                  )}{" "}
                  %
                </strong>

                {zadaneRiadky.length > 0 &&
                  !vlastnaVolbaPlatna && (
                    <span>
                      {" "}
                      Vyplňte všetky údaje a
                      súčet 100 %.
                    </span>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      <InvesticneOdporucaniaVystupKarta
        variantKluc={variantKluc}
        stav={stav}
        zvoleneProdukty={
          zvoleneProdukty
        }
        maProduktovyVyber={
          maProduktovyVyber
        }
        maVlastnuVolbu={
          maVlastnuVolbu
        }
        vlastnaVolbaJeVybrana={
          vlastnaVolbaJeVybrana
        }
        vlastnaVolbaProdukt={
          vlastnaVolbaProdukt
        }
        zadaneRiadky={
          zadaneRiadky
        }
        vahyRieseni={aktivneVahy}
        aktivnyVariant={
          aktivnyVariant
        }
        analyzaVyberu={
          analyzaVyberu
        }
        onUpravitVahy={
          otvorUpravuVah
        }
      />

      <ModalVah
        modal={modalVah}
        onZmena={zmenModalVahu}
        onZmenaIgnorovania={
          zmenIgnorovanieMinimalnychInvesticii
        }
        onZrusit={zrusModalVah}
        onPotvrdit={
          potvrdModalVah
        }
      />
    </>
  );
}
  );

export default InvesticneOdporucaniaVstupKarta;